/* The canonical team list — designs/canonical-teams.md, reshaped by
   designs/teams-and-products.md (Oct 1 2026). ONE controlled vocabulary for
   "which team", spoken by intake submissions, owners, stakeholder +1s and
   the Teams section. DISTINCT from the Entra roster in teams.ts, which the
   Org section serves (intake-foldin.md IF2.6 — HR-B5 guard-6).

   TP1: a team is a platform team, a product team (one team per PDP, a
   market product line), or other (a customer program or prototype work). A
   team may own products (products.ts) — a product or other team gets its
   first automatically, a platform team only by an Admin — each carrying its
   market, its vehicles and its PDP; a platform team has none. The market
   lives on the PRODUCT now, not on the team. */

import type { Stamp, UserRef } from "./organizer-roles";
import { ENTERED_SRC } from "./projects";
import { normalizeName, slugId } from "./roles";

export const TEAM_KINDS = ["platform", "product", "other"] as const;
export type TeamKind = (typeof TEAM_KINDS)[number];

/* the three kinds as people read them (TP1) */
export const TEAM_KIND_LABELS: Readonly<Record<TeamKind, string>> = {
  platform: "Platform team",
  product: "Product team",
  other: "Other team",
};

/* the pickers' and lists' group headings, in display order (TP1) */
export const TEAM_GROUPS: readonly { kind: TeamKind | null; label: string }[] = [
  { kind: "platform", label: "Platform teams" },
  { kind: "product", label: "Product teams" },
  { kind: "other", label: "Other teams" },
  /* a form-added team an Admin has not filed yet (TP5) */
  { kind: null, label: "Not filed yet" },
];
export const teamGroupLabel = (kind: TeamKind | null): string =>
  TEAM_GROUPS.find((g) => g.kind === kind)?.label ?? "Not filed yet";
/* a stable sort into TEAM_GROUPS order, keeping the list's own order within */
export const byTeamGroup = <T extends { kind: TeamKind | null }>(teams: readonly T[]): T[] => {
  const rank = (k: TeamKind | null): number => TEAM_GROUPS.findIndex((g) => g.kind === k);
  return [...teams].sort((a, b) => rank(a.kind) - rank(b.kind));
};

/* a product or other team gets its FIRST product automatically (TP2) */
export const hasProduct = (kind: TeamKind | null): boolean => kind === "product" || kind === "other";

/* any filed team may OWN products — platform teams included: PDP 2.5 walks
   platform products (PRAK, a Mobius product) through the same process, so
   an Admin adds them by hand (TP2, Lewis Oct 2 2026) */
export const ownsProducts = (team: { kind: TeamKind | null; retired?: boolean }): boolean =>
  team.kind !== null && team.retired !== true;

export interface CanonicalTeam {
  /* stable slug — the storage key and the vocabulary word (CT3). Seed slugs
     are fixed identifiers, not derived from the display name. */
  id: string;
  name: string;
  /* null = NOT RECORDED: a team added from the intake form, waiting for an
     Admin to say what it is (TP5) — never guessed */
  kind: TeamKind | null;
  /* added from the form and not yet filed by an Admin (TP5) */
  needsReview?: boolean;
  /* out of circulation, never deleted (CT2) — absent means in circulation */
  retired?: boolean;
  /* folded into another team by an Admin merge (TP6): the id of the team
     that now speaks for this one. A merged team is also retired. */
  mergedInto?: string | null;
  recorded: Stamp;
}

/* what an actor submits — the id and the stamp are the app's to mint. A
   requester sends only the name (kind not recorded, needs review); an Admin
   may say the kind up front (TP5). */
export interface CanonicalTeamInput {
  name: string;
  kind?: TeamKind | null;
}

/* a partial correction, Admin-only; the id never re-slugs (CT5) */
export interface CanonicalTeamEdit {
  name?: string;
  kind?: TeamKind | null;
  retired?: boolean;
  needsReview?: boolean;
  /* set ONLY by the merge route (TP6) — never read off a request body */
  mergedInto?: string | null;
}

/* the seed rows as migrations 001 and 009 generated their SQL from them —
   FROZEN in this shape (market included) because `checksumOf` hashes those
   statements (SM3). Live teams no longer carry a market (TP2). */
export interface SeedTeamRow {
  id: string;
  name: string;
  kind: "platform" | "product";
  market: string | null;
}

/* The operating-model roster (council msg 15449): 4 platform + 13 product.
   Seeded once by migration 001; the memory store plants the same list so
   both stores answer alike (CT8). */
export const TEAM_SEED_SRC = "operating-model roster (2026-08-29)";
export const TEAM_SEED_DATE = "2026-08-29T00:00:00.000Z";

export const SEED_TEAMS: readonly SeedTeamRow[] = [
  { id: "perception-platform", name: "Perception Platform", kind: "platform", market: null },
  { id: "gnc-platform", name: "GNC Platform", kind: "platform", market: null },
  { id: "electrical-mechanical", name: "Electrical Mechanical", kind: "platform", market: null },
  { id: "embedded", name: "Embedded", kind: "platform", market: null },
  { id: "logistics", name: "Logistics (Yard Truck)", kind: "product", market: "Logistics" },
  { id: "reach-stacker", name: "Reach Stacker", kind: "product", market: "Material Handling" },
  { id: "telehandler", name: "Telehandler", kind: "product", market: "Material Handling" },
  { id: "ctl", name: "CTL", kind: "product", market: "Material Handling" },
  { id: "ag-tractor", name: "Ag Tractor (8R / 9R)", kind: "product", market: "Ag" },
  { id: "dozer", name: "Dozer", kind: "product", market: "Construction" },
  { id: "compactor", name: "Compactor", kind: "product", market: "Construction" },
  { id: "wheel-loader", name: "Wheel Loader", kind: "product", market: "Construction" },
  { id: "adt-water-truck", name: "ADT / Water Truck", kind: "product", market: "Construction" },
  { id: "blitz-excavator", name: "Blitz — Excavator (CAT 352)", kind: "product", market: "Construction" },
  { id: "blitz-hm460", name: "Blitz — Komatsu HM460", kind: "product", market: "Construction" },
  { id: "fairway-mower", name: "Landscaping Fairway Mower", kind: "product", market: "Landscaping" },
  { id: "commercial-landscaping", name: "Commercial Landscaping (Scythe)", kind: "product", market: "Landscaping" },
];

/* The three delivering bodies the roster missed (per Lewis, Sep 2026):
   Mobius Platform — a real platform team that had a domain tab but no team
   entry, the gap intake-multi-domain.md MD10 named and deliberately left —
   and PAT / AP, which Lewis confirms have run their own delivery chains.
   Without these, an ask in those domains cannot NAME the team expected to
   deliver it (intake-ownership-model.md OM4a).

   A SEPARATE list, never appended to SEED_TEAMS: migration 001's seed SQL is
   generated from SEED_TEAMS, and `checksumOf` hashes the statements — so
   editing SEED_TEAMS changes 001's checksum and every populated database,
   production included, REFUSES TO BOOT (SM3). These land as migration 009
   instead, and are planted here too so both stores answer alike (CT8).

   `kind: "platform"` for all three: the kind exists to say whether a team
   records a market (CT3), and none of these do. AP's slug is `ap` and slugs
   never re-slug (CT5) — its identity is still the open question named in
   intake-governance-domains.md GD6, so if AP turns out to BE VIP the display
   name is corrected and the id stays. Recorded as a guess, not hidden. */
export const ADDED_TEAMS_SRC = "delivering bodies added to the roster (2026-09-07)";
export const ADDED_TEAMS_DATE = "2026-09-07T00:00:00.000Z";

export const ADDED_TEAMS: readonly SeedTeamRow[] = [
  { id: "mobius-platform", name: "Mobius Platform", kind: "platform", market: null },
  { id: "pat", name: "PAT", kind: "platform", market: null },
  { id: "ap", name: "AP", kind: "platform", market: null },
];

/* Name CORRECTIONS to seeded teams (CT5, per Lewis Sep 2026). The roster
   named four platform teams inconsistently — "Perception Platform" and "GNC
   Platform" carried the word, "Electrical Mechanical" and "Embedded" did not
   — so a reader picking delivery owners saw two of the four look like
   something other than a platform team.

   A correction, not a re-seed: the id NEVER re-slugs (CT5), so
   `electrical-mechanical` and `embedded` keep their slugs and every stored
   reference to them keeps resolving. Only the display name moves.

   And NOT an edit to SEED_TEAMS, for the same reason ADDED_TEAMS is separate:
   migration 001's seed SQL is generated from that constant and `checksumOf`
   hashes the statements, so editing it changes 001's checksum and every
   populated database refuses to boot (SM3). Postgres gets these as appended
   revisions in migration 010; this overlay keeps the memory store identical
   (CT8).

   Bonus, worth knowing: "Embedded" was ALSO the Embedded domain's name, so
   the same string meant a team in one control and a domain in another. The
   correction removes that ambiguity. */
export const TEAM_RENAMES_SRC = "platform teams named consistently (2026-09-07)";
export const TEAM_RENAMES_DATE = "2026-09-07T00:00:00.000Z";

export const TEAM_RENAMES: readonly { id: string; name: string }[] = [
  { id: "electrical-mechanical", name: "Electrical Mechanical Platform" },
  { id: "embedded", name: "Embedded Platform" },
];

/* ── The Oct 1 2026 consolidation (designs/teams-and-products.md TP7, per
   Lewis). Every product row of the old list is a team with its own PDP,
   except where two rows were one team's vehicles (Blitz). Applied to
   Postgres as appended revisions by migration 022; planted here so the
   memory store answers alike (CT8). ── */
export const CONSOLIDATION_SRC = "teams and products consolidated (2026-10-01)";
export const CONSOLIDATION_DATE = "2026-10-01T00:00:00.000Z";

/* name and kind corrections; the ids never re-slug (CT5) */
export const CONSOLIDATION_RENAMES: readonly { id: string; name: string; kind: TeamKind }[] = [
  /* AP is the Advanced Prototype Team (Lewis, Oct 1 2026) — GD6 answered */
  { id: "ap", name: "Advanced Prototype Team", kind: "platform" },
  { id: "logistics", name: "Logistics", kind: "product" },
  /* 8R and 9R are vehicles of ONE Ag team */
  { id: "ag-tractor", name: "Ag", kind: "product" },
  /* a customer program, not a market product line */
  { id: "commercial-landscaping", name: "Scythe", kind: "other" },
];

/* Blitz: the two Blitz rows were one program's two vehicles */
export const CONSOLIDATION_ADDED: readonly { id: string; name: string; kind: TeamKind }[] = [
  { id: "blitz", name: "Blitz", kind: "other" },
];
export const CONSOLIDATION_MERGES: readonly { from: string; into: string }[] = [
  { from: "blitz-excavator", into: "blitz" },
  { from: "blitz-hm460", into: "blitz" },
];

const applyConsolidation = (teams: CanonicalTeam[]): CanonicalTeam[] => {
  const stamp = { source: CONSOLIDATION_SRC, date: CONSOLIDATION_DATE };
  const out = teams.map((t): CanonicalTeam => {
    const renamed = CONSOLIDATION_RENAMES.find((r) => r.id === t.id);
    if (renamed) return { ...t, name: renamed.name, kind: renamed.kind, recorded: stamp };
    const merged = CONSOLIDATION_MERGES.find((m) => m.from === t.id);
    if (merged) return { ...t, retired: true, mergedInto: merged.into, recorded: stamp };
    return t;
  });
  return [...out, ...CONSOLIDATION_ADDED.map((t) => ({ ...t, recorded: stamp }))];
};

export const seedCanonicalTeams = (): CanonicalTeam[] =>
  applyConsolidation([
    ...SEED_TEAMS.map(({ market: _market, ...t }) => {
      const renamed = TEAM_RENAMES.find((r) => r.id === t.id);
      return renamed
        ? { ...t, name: renamed.name, recorded: { source: TEAM_RENAMES_SRC, date: TEAM_RENAMES_DATE } }
        : { ...t, recorded: { source: TEAM_SEED_SRC, date: TEAM_SEED_DATE } };
    }),
    ...ADDED_TEAMS.map(({ market: _market, ...t }) => ({
      ...t,
      recorded: { source: ADDED_TEAMS_SRC, date: ADDED_TEAMS_DATE },
    })),
  ]);

const NAME_MAX = 200;

const nameReason = (name: unknown): string | null => {
  if (typeof name !== "string" || !normalizeName(name)) return "name is required";
  if (name.length > NAME_MAX) return `name is over ${NAME_MAX} characters`;
  return null;
};

const KIND_ERROR = 'kind must be "platform", "product", "other", or null (not recorded)';
const kindReason = (kind: unknown): string | null =>
  kind === undefined || kind === null || TEAM_KINDS.includes(kind as TeamKind) ? null : KIND_ERROR;

/* one name, one identity (CT5): a clash names the standing team so the
   caller can +1 the vocabulary instead of forking it */
const clashReason = (name: string, existing: CanonicalTeam[], exceptId?: string): string | null => {
  const id = slugId(name);
  const wanted = normalizeName(name);
  const clash = existing.find(
    (t) => t.id !== exceptId && (t.id === id || normalizeName(t.name) === wanted),
  );
  return clash ? `already exists as "${clash.name}" (${clash.id}) — edit it instead` : null;
};

export function validateCanonicalTeamInput(
  input: CanonicalTeamInput,
  existing: CanonicalTeam[],
): string | null {
  return nameReason(input?.name) ?? kindReason(input.kind) ?? clashReason(input.name, existing);
}

export function validateCanonicalTeamEdit(
  edit: CanonicalTeamEdit,
  existing: CanonicalTeam[],
  id: string,
): string | null {
  if (
    edit.name === undefined &&
    edit.kind === undefined &&
    edit.retired === undefined &&
    edit.needsReview === undefined
  )
    return "nothing to change — provide name, kind, retired, or needsReview";
  if (edit.name !== undefined) {
    const bad = nameReason(edit.name) ?? clashReason(edit.name, existing, id);
    if (bad) return bad;
  }
  const kind = kindReason(edit.kind);
  if (kind) return kind;
  if (edit.retired !== undefined && typeof edit.retired !== "boolean")
    return "retired must be a boolean";
  if (edit.needsReview !== undefined && typeof edit.needsReview !== "boolean")
    return "needsReview must be a boolean";
  return null;
}

/* the correction is a NEW revision carrying the actor's stamp (CT2) */
export const applyCanonicalTeamEdit = (
  current: CanonicalTeam,
  edit: CanonicalTeamEdit,
  by: UserRef,
  date: string,
): CanonicalTeam => {
  return {
    ...current,
    ...(edit.name !== undefined ? { name: edit.name.trim() } : {}),
    ...(edit.kind !== undefined ? { kind: edit.kind } : {}),
    ...(edit.retired !== undefined ? { retired: edit.retired } : {}),
    ...(edit.needsReview !== undefined ? { needsReview: edit.needsReview } : {}),
    ...(edit.mergedInto !== undefined ? { mergedInto: edit.mergedInto } : {}),
    recorded: { source: ENTERED_SRC, date, by },
  };
};
